import * as pdfjs from 'pdfjs-dist';

const PDFJS_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

export interface EmisorFacturaCompraPdf {
  nombre?: string;
  cuit?: string;
  domicilio?: string;
}

interface PdfTextItem {
  x: number;
  y: number;
  str: string;
}

const CUIT_DIGITS_RE = /^\d{11}$/;

interface PdfJsTextItem {
  str?: string;
  transform: number[];
}

function configurePdfWorker(): void {
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
  }
}

function toTextItems(rawItems: unknown[]): PdfTextItem[] {
  return rawItems
    .filter((item): item is PdfJsTextItem => {
      if (!item || typeof item !== 'object') return false;
      const candidate = item as PdfJsTextItem;
      return typeof candidate.str === 'string' && Boolean(candidate.str.trim()) && Array.isArray(candidate.transform);
    })
    .map((item) => ({
      x: Math.round(item.transform[4]),
      y: Math.round(item.transform[5]),
      str: item.str!.trim(),
    }));
}

function findValueOnRow(items: PdfTextItem[], label: PdfTextItem, minX: number, maxX: number): string[] {
  const sameRow = items.filter(
    (i) => Math.abs(i.y - label.y) <= 4 && i.x >= minX && i.x <= maxX && !i.str.includes(':'),
  );
  const nextRows = items.filter(
    (i) => i.y < label.y - 4 && i.y > label.y - 24 && i.x >= minX && i.x <= maxX && !i.str.includes(':'),
  );
  return [...sameRow, ...nextRows].sort((a, b) => b.y - a.y || a.x - b.x).map((i) => i.str);
}

function extractEmisorFromItems(items: PdfTextItem[]): EmisorFacturaCompraPdf | null {
  if (items.length === 0) return null;

  let cuit: string | undefined;
  const emisorCuitLabel = items.find((i) => i.str === 'CUIT:' && i.x > 250 && i.y > 680);
  if (emisorCuitLabel) {
    const cuitItem = items.find(
      (i) =>
        Math.abs(i.y - emisorCuitLabel.y) <= 3 &&
        i.x > 300 &&
        CUIT_DIGITS_RE.test(i.str.replace(/\D/g, '')),
    );
    if (cuitItem) {
      cuit = cuitItem.str.replace(/\D/g, '');
    }
  }

  let nombre: string | undefined;
  const razonLabel = items.find((i) => i.str === 'Razón Social:' && i.x < 50 && i.y > 680);
  if (razonLabel) {
    const parts = findValueOnRow(items, razonLabel, 50, 280);
    nombre = parts.join(' ').trim() || undefined;
  }

  let domicilio: string | undefined;
  const domicilioLabel = items.find((i) => i.str === 'Domicilio Comercial:' && i.x < 50 && i.y > 680);
  if (domicilioLabel) {
    const parts = findValueOnRow(items, domicilioLabel, 100, 320);
    domicilio = parts.join(' ').trim() || undefined;
  }

  if (!nombre && !cuit && !domicilio) return null;
  return { nombre, cuit, domicilio };
}

/** Extrae datos del emisor (proveedor) usando posición en el PDF AFIP. */
export async function extraerEmisorFacturaCompraPdf(archivo: File): Promise<EmisorFacturaCompraPdf | null> {
  configurePdfWorker();
  const buffer = await archivo.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(buffer) }).promise;
  const page = await doc.getPage(1);
  const content = await page.getTextContent();
  return extractEmisorFromItems(toTextItems(content.items));
}

/** Formatea CUIT de 11 dígitos para visualización. */
export function formatCuit11(cuit?: string | null): string | undefined {
  const digits = (cuit ?? '').replace(/\D/g, '');
  if (digits.length !== 11) return cuit?.trim() || undefined;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}
